// Hook de analytics fire-and-forget com buffer + debounce.
// Não bloqueia UI. Falhas são silenciosas.

import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import { getSessionId, getDevice, getUtm } from "@/utils/analyticsSession";

export type AnalyticsEventType =
  | "page_view"
  | "whatsapp_click"
  | "plan_view"
  | "checkout_click"
  | "signup_started"
  | "signup_completed"
  | "login"
  | "tool_access"
  | "direct_payment_page_view"
  | "direct_payment_whatsapp_click"
  | "direct_payment_nubank_click"
  | "direct_payment_pix_click"
  | "direct_payment_card_click";

interface QueueItem {
  event_type: AnalyticsEventType;
  path: string | null;
  user_id: string | null;
  session_id: string;
  device: "mobile" | "desktop";
  referrer: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

const buffer: QueueItem[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const DEBOUNCE_MS = 2000;
const MAX_BUFFER = 10;

async function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (buffer.length === 0) return;
  const batch = buffer.splice(0, buffer.length);
  try {
    await supabase.from("analytics_events" as any).insert(batch as any);
  } catch {
    /* silent */
  }
}

if (typeof window !== "undefined") {
  // Flush ao fechar/ocultar a aba
  const handler = () => {
    if (document.visibilityState === "hidden") void flush();
  };
  window.addEventListener("visibilitychange", handler);
  window.addEventListener("pagehide", () => void flush());
}

export function useAnalytics() {
  const { user } = useAuth();

  const track = useCallback(
    (event: AnalyticsEventType, metadata?: Record<string, unknown>) => {
      try {
        const utm = getUtm();
        const item: QueueItem = {
          event_type: event,
          path: typeof window !== "undefined" ? window.location.pathname : null,
          user_id: user?.id || null,
          session_id: getSessionId(),
          device: getDevice(),
          referrer: typeof document !== "undefined" ? document.referrer || null : null,
          utm_source: utm.utm_source || null,
          utm_medium: utm.utm_medium || null,
          utm_campaign: utm.utm_campaign || null,
          metadata: metadata || null,
          created_at: new Date().toISOString(),
        };
        buffer.push(item);
        if (buffer.length >= MAX_BUFFER) {
          void flush();
        } else {
          if (flushTimer) clearTimeout(flushTimer);
          flushTimer = setTimeout(() => void flush(), DEBOUNCE_MS);
        }
      } catch {
        /* silent */
      }
    },
    [user?.id]
  );

  return { track };
}
